import React, { useCallback, useEffect, useState } from "react";
import { Input } from "@/app/global-components/input";
import {
  MenuItem,
  OutlinedInput,
  Select,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Switch,
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { RootState } from "@/store/type";
import { useSelector } from "react-redux";
import { debounce, isEmpty, isNull } from "lodash";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import ProductCard from "@/pages/articles/components/ProductCard";
import AddNewItem from "@/pages/articles/components/AddnewItem";
import useRegisterCompany from "@/hooks/use-register-company";
import useItems from "@/hooks/use-item";
import { showToast } from "@/app/global-components/toaster";
import useOffer from "@/hooks/useTender";
import { LANG } from "@/types";
import {
  Pencil,
  Trash2,
  Upload,
  X,
  FileSpreadsheet,
  Download,
  Image,
  ImagePlus,
  Search,
  Filter,
  Plus,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/app/global-components/button";
import { Editor } from "@tinymce/tinymce-react";
import { Editor as EditorType, EditorEvent } from "tinymce";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/app/global-components/tabs";
import { useForm } from "react-hook-form";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import TableViewIcon from "@mui/icons-material/TableView";
import { cn } from "@/lib/utils";
import { NoImage } from "@/assets";
import { useDropzone } from "react-dropzone";
import { Form, FormLabel } from "@/app/global-components/form";
import TableComponent from "@/app/global-components/table";

export interface CategoryOption {
  value: any;
  label: any;
}
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export enum ACTION {
  ADD = "add",
  EDIT = "edit",
}
export type EditItem = {
  id: string;
  name: string;
  price: number;
  descriptionEn: string;
  categories: string[] | string;
  descEn: string;
  descriptionAr: string;
  descAr: string;
  minOrder: number;
  imagePreview?: string;
  countryOfOrigin: string;
  leadTime: string;
  code: string;
  vat: string;
  unit: string;
  type: string;
  brand: string;
  pdfLink: string;
  sfdaMdma: string;
};

export interface Item {
  id: string;
  name: string;
  code: string;
  brand: string;
  unit: string;
  categories: string;
  price: number;
  vat: number;
  companyId: string;
  userId: string;
  ItemNR: number;
  descriptionEn: string;
  descriptionAr: string;
  images: any[];
  video: string;
  isVisible: boolean;
  image: {
    url: string;
    key: string;
    _id: string;
  };
  status: string;
  minOrder: number;
  isPublished: boolean;
  descEn: string;
  descAr: string;
  type: string;
  countryOfOrigin: string;
  leadTime: string;
  pdfLink: string;
  sfdaMdma: string;
}

const ArticlesList: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  // const isMobile = useMediaQuery("(max-width:600px)");
  const [searchTerm, setSearchTerm] = useState("");
  const [items, setItems] = useState<Item[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [Categoris, setCategoris] = useState<CategoryOption[]>([]);
  const [filterCategories, setFilterCategories] = useState<string[] | string>(
    []
  );

  const myCompany = useSelector((state: RootState) => state.auth.myCompany);

  const {
    fetchItems,
    publishItem,
    fetchItemSearch,
    updateItem,
    deleteItem,
    error,
    importItems,
  } = useItems();
  const [isLoading, setIsLoading] = useState(false);
  const { getCompanyMine } = useRegisterCompany();
  const { getcategories } = useOffer();
  const [MediaModalOpen, setMediaModalOpen] = useState<EditItem | null>(null);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState(true);
  const [selectedItem, setSelectedItem] = useState<EditItem | null>(null);
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
  const [selectedExcelFile, setSelectedExcelFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  const form = useForm({
    defaultValues: {
      minOrder: 1,
      descriptionEn: "",
      descriptionAr: "",
      images: [] as File[],
      video: null as File | null,
    },
  });

  useEffect(() => {
    if (MediaModalOpen) {
      form.setValue(
        "descriptionEn",
        MediaModalOpen.descriptionEn || MediaModalOpen.descEn
      );
      form.setValue(
        "descriptionAr",
        MediaModalOpen.descriptionAr || MediaModalOpen.descAr
      );
      form.setValue("minOrder", MediaModalOpen.minOrder);
    }
  }, [MediaModalOpen]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles: File[]) => {
      const newPreviewImages = acceptedFiles.map((file) =>
        URL.createObjectURL(file)
      );
      const updatedPreviews = [...previewImages, ...newPreviewImages];
      const updatedFiles = [...imageFiles, ...acceptedFiles];

      setPreviewImages(updatedPreviews);
      setImageFiles(updatedFiles);
      form.setValue("images", updatedFiles);
    },
    accept: {
      "image/*": [".jpeg", ".png", ".jpg", ".gif", ".webp"],
    },
    maxFiles: 5,
  });

  const removeImage = (index: number) => {
    const updatedPreviews = previewImages.filter((_, i) => i !== index);
    const updatedFiles = imageFiles.filter((_, i) => i !== index);
    setPreviewImages(updatedPreviews);
    setImageFiles(updatedFiles);
    form.setValue("images", updatedFiles);
  };

  const handleMediaSubmit = async (data: any) => {
    try {
      setIsLoading(true);
      const response = await updateItem({
        ...data,
        minOrder: form.watch("minOrder"),
        price: MediaModalOpen?.price,
        name: MediaModalOpen?.name,
        id: MediaModalOpen?.id, // You'll need to get this from your item data
      });

      if (response) {
        showToast(t("alerts.success.itemSaved"), "success");
        setMediaModalOpen(null);
        publishItem(MediaModalOpen?.id, true);
        form.reset();
        setPreviewImages([]);
        setImageFiles([]);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showToast("Failed to save item", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetching = async () => {
      const Categories = await getcategories();
      if (Categories) {
        const uniqueCategories = Categories.data.filter(
          (item: { name: any }, index: any, self: any[]) =>
            index === self.findIndex((t) => t.name === item.name)
        );
        const Cates: CategoryOption[] = uniqueCategories.map(
          (item: { id: any; name: any }) => ({
            value: item?.id,
            label: item.name,
          })
        );
        setCategoris(Cates);
      } else {
        setCategoris([]);
      }
      const res = await fetchItemSearch(searchTerm, myCompany?.id);
      if (res) {
        setItems(res.data);
      }
    };
    fetching();
  }, [searchTerm]);
  const handleAddItem = async (newItem: any) => {
    setIsLoading(true);
    const response = await updateItem({
      ...newItem,
      ItemNR: parseInt(isNull(myCompany.itemNr) ? 0 : myCompany.itemNr) + 1,
    });
    if (response) {
      loadItems();
      setIsModalOpen(false);
      showToast(t("alerts.success.itemSaved"), "success");
    }
    setIsLoading(false);
  };
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      setSearchTerm(searchTerm);
    }, 500),
    []
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedSearch(value);
  };

  const handleDeleteItem = async (id: string) => {
    setItemToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      const response = await deleteItem(itemToDelete);
      if (response) {
        showToast(t("alerts.success.itemDeleted"), "success");
        setItems((prev) => prev.filter((item) => item?.id !== itemToDelete));
      }
    }
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const loadItems = async () => {
    const items = await fetchItems({});
    setItems(items.data);
  };
  useEffect(() => {
    if (!isEmpty(myCompany)) {
      if (!isNull(myCompany)) {
        loadItems();
      }
    }
  }, [myCompany]);

  useEffect(() => {
    getCompanyMine();
  }, []);

  const handleVideoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setVideoPreview(URL.createObjectURL(file));
      form.setValue("video", file);
    }
  };

  const removeVideo = () => {
    setVideoPreview(null);
    form.setValue("video", null);
  };

  const handlePublish = async (id: string, isPublished: boolean) => {
    const response = await publishItem(id, isPublished);
    if (response) {
      loadItems();
      showToast(t("alerts.success.itemPublished"), "success");
    }
  };
  const setEditMode = (item: EditItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  // Excel import functions
  const handleExcelFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check if file is Excel
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ];

      if (
        validTypes.includes(file.type) ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls") ||
        file.name.endsWith(".csv")
      ) {
        setSelectedExcelFile(file);
      } else {
        showToast(t("alerts.error.invalidFileType"), "error");
      }
    }
  };

  const handleExcelImport = async () => {
    if (!selectedExcelFile) {
      showToast(t("alerts.error.noFileSelected"), "error");
      return;
    }

    try {
      setIsImporting(true);
      const response = await importItems(selectedExcelFile);

      if (response) {
        showToast(t("alerts.success.itemsImported"), "success");
        setIsExcelModalOpen(false);
        setSelectedExcelFile(null);
        loadItems(); // Refresh the items list
      }
    } catch (error) {
      console.error("Error importing Excel file:", error);
      showToast(t("alerts.error.importFailed"), "error");
    } finally {
      setIsImporting(false);
    }
  };

  const downloadExcelTemplate = () => {
    // Create a sample Excel template with headers
    const headers = [
      "ItemNR",
      "name",
      "manufacturer",
      "brand",
      "model",
      "description",
      "price",
      "categories",
      "vat",
      "minOrder",
      "unit",
      "type",
      "countryOfOrigin",
      "leadTime",
      "pdfLink",
      "sfdaMdma",
    ];

    const sampleData = [
      "1",
      "Sample Product Name",
      "Sample Manufacturer",
      "Sample Brand",
      "Sample Model",
      "Sample Description",
      "100.00",
      "Category1,Category2",
      "15",
      "10",
      "piece",
      "Machine",
      "Saudi Arabia",
      "2-3 weeks",
      "https://example.com/pdf",
      "SFDA123456",
    ];

    const csvContent = [headers.join(","), sampleData.join(",")].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "products_import_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const exportItems = () => {
    if (!items || items.length === 0) {
      showToast(t("alerts.error.noItemsToExport"), "error");
      return;
    }

    // Define headers for export
    const headers = [
      "ItemNR",
      "name",
      "code",
      "brand",
      "unit",
      "categories",
      "price",
      "vat",
      "minOrder",
      "type",
      "countryOfOrigin",
      "leadTime",
      "pdfLink",
      "sfdaMdma",
      "descriptionEn",
      "descriptionAr",
      "descEn",
      "descAr",
      "isPublished",
      "status",
      "isVisible",
    ];

    // Convert items to CSV format
    const csvData = items.map((item) =>
      [
        item.ItemNR || "",
        `"${item.name || ""}"`,
        item.code || "",
        `"${item.brand || ""}"`,
        item.unit || "",
        `"${item.categories || ""}"`,
        item.price || "",
        item.vat || "",
        item.minOrder || "",
        `"${item.type || ""}"`,
        `"${item.countryOfOrigin || ""}"`,
        `"${item.leadTime || ""}"`,
        `"${item.pdfLink || ""}"`,
        `"${item.sfdaMdma || ""}"`,
        `"${item.descriptionEn || ""}"`,
        `"${item.descriptionAr || ""}"`,
        `"${item.descEn || ""}"`,
        `"${item.descAr || ""}"`,
        item.isPublished ? "true" : "false",
        `"${item.status || ""}"`,
        item.isVisible ? "true" : "false",
      ].join(",")
    );

    // Combine headers and data
    const csvContent = [headers.join(","), ...csvData].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `products_export_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    showToast("Items exported successfully", "success");
  };
  const columns = [
    {
      title: (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          {t("item.fields.image")}
        </div>
      ),
      field: "image",
      render: (item: Item) => (
        <img
          src={item.images[0] || NoImage}
          alt={item.name}
          className="w-14 h-14 border border-zinc-300 rounded-lg object-cover"
        />
      ),
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          {t("item.fields.code")}
        </div>
      ),
      field: "code",
      render: (item: Item) => (
        <div className="font-medium text-gray-900">{item.code || "_"}</div>
      ),
      sortable: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
          {t("checkout.fields.name")}
        </div>
      ),
      field: "name",
      render: (item: Item) => (
        <div className="font-medium text-gray-900 max-w-xs truncate">
          {item.name || "_"}
        </div>
      ),
      sortable: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
          {t("checkout.fields.unitPrice")}
        </div>
      ),
      field: "price",
      render: (item: Item) => (
        <div className="font-semibold text-purple-700">
          {item.price} {t("currency")}
        </div>
      ),
      sortable: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
          {t("item.fields.type")}
        </div>
      ),
      field: "type",
      render: (item: Item) => (
        <div className="text-gray-700">{item.type || "-"}</div>
      ),
      hideOnMobile: true,
      sortable: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
          {t("item.fields.vat")}
        </div>
      ),
      field: "vat",
      render: (item: Item) => <div className="text-gray-700">{item.vat}%</div>,
      hideOnMobile: true,
      sortable: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
          {t("product_form.isPublished")}
        </div>
      ),
      field: "isPublished",
      render: (item: Item) => (
        <Switch
          onChange={(e) => {
            handlePublish(item?.id, e.target.checked);
          }}
          checked={item.isPublished}
        />
      ),
      hideOnMobile: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
          {t("actions.actions")}
        </div>
      ),
      field: "actions",
      render: (item: Item) => (
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setEditMode(item as unknown as EditItem)}
            size="sm"
            variant="outline"
            className="h-8 w-8 p-0"
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            onClick={() => setMediaModalOpen(item as unknown as EditItem)}
            size="sm"
            variant="outline"
            className="h-8 w-8 p-0"
          >
            <Image className="h-3 w-3" />
          </Button>
          <Button
            onClick={() => handleDeleteItem(item.id)}
            size="sm"
            variant="outline"
            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Box
      sx={{
        py: 4,
        px: { xs: 2, sm: 3, md: 4 },
        background:
          "linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f1f5f9 100%)",
        minHeight: "100vh",
      }}
    >
      {/* Header Section */}
      <Box className="flex justify-between items-center">
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
            <Box
              sx={{
                width: 4,
                height: 32,
                background: "linear-gradient(135deg, #644980 0%, #1d4ed8 100%)",
                borderRadius: 2,
                mr: 2,
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: "1.2rem", md: "2rem" },
                fontWeight: 700,
                background: "linear-gradient(135deg, #1f2937 0%, #374151 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {t("products.title")}
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: "#6b7280", ml: 6 }}>
            {t("statistics.quotationsAvailable")}: {items.length}
          </Typography>
        </Box>
        <Box
          sx={{ mb: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}
        >
          <Tooltip title="Export Items" arrow>
            <IconButton
              onClick={exportItems}
              disabled={!items || items.length === 0}
              sx={{
                backgroundColor: "#f3f4f6",
                color: "#6b7280",
                borderRadius: 2,
                p: 1.5,
                "&:hover": {
                  backgroundColor: "#e5e7eb",
                  color: "#374151",
                },
                "&:disabled": {
                  backgroundColor: "#f9fafb",
                  color: "#d1d5db",
                },
              }}
            >
              <Download size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("actions.importExcel")} arrow>
            <IconButton
              onClick={() => setIsExcelModalOpen(true)}
              sx={{
                backgroundColor: "#f3f4f6",
                color: "#6b7280",
                borderRadius: 2,
                p: 1.5,
                "&:hover": {
                  backgroundColor: "#e5e7eb",
                  color: "#374151",
                },
              }}
            >
              <FileSpreadsheet size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("actions.addItem")} arrow>
            <IconButton
              onClick={() => setIsModalOpen(true)}
              sx={{
                background: "#18181b",
                color: "white",
                p: 2,
                borderRadius: 3,
                "&:hover": {
                  background: "#18181b",
                },
                transition: "all 0.3s ease",
              }}
            >
              <Plus size={20} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Card
        sx={{ mb: 4, borderRadius: 3, boxShadow: "none", overflow: "hidden" }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ position: "relative" }}>
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder={t("actions.searching")}
                  className="pl-10 pr-4 py-3 bg-gray-50 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full text-sm transition-all duration-200 hover:bg-white"
                  onChange={handleChange}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Select
                displayEmpty
                id="categorie-multiple-name"
                input={<OutlinedInput />}
                inputProps={{ "aria-label": "Without label" }}
                multiple
                sx={{
                  minWidth: 300,
                  borderRadius: 2,
                  backgroundColor: "#ffffff",
                  "& .MuiSelect-select": {
                    paddingRight: "10px !important",
                    paddingLeft: "12px !important",
                    paddingTop: "12px !important",
                    paddingBottom: "12px !important",
                    minHeight: "20px !important",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                  },
                  "& .MuiSelect-icon": {
                    left: locale === LANG.EN ? "unset" : 0,
                    marginLeft: 0,
                    right: locale === LANG.EN ? 0 : "unset",
                    color: "#6b7280",
                  },
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#644980",
                    },
                  },
                  "&.Mui-focused": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#644980",
                      borderWidth: 2,
                    },
                  },
                }}
                renderValue={(selected: any) => {
                  if (selected.length === 0) {
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "#9ca3af",
                        }}
                      >
                        <Filter size={16} />
                        <em>{t("categories.select")}</em>
                      </Box>
                    );
                  }

                  if (selected.length <= 2) {
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value: any) => {
                          const category = Categoris.find(
                            (cat) => cat.value === value
                          );
                          return category ? (
                            <Box
                              key={value}
                              sx={{
                                backgroundColor: "#e0e7ff",
                                color: "#3730a3",
                                border: "1px solid #c7d2fe",
                                borderRadius: 1,
                                px: 1,
                                py: 0.5,
                                fontSize: "0.75rem",
                              }}
                            >
                              {category.label}
                            </Box>
                          ) : null;
                        })}
                      </Box>
                    );
                  }

                  return (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "#644980",
                          color: "white",
                          border: "1px solid #644980",
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          fontSize: "0.75rem",
                        }}
                      >
                        {selected.length} {t("categories.title")}
                      </Box>
                    </Box>
                  );
                }}
                value={filterCategories}
                onChange={(e) => setFilterCategories(e.target.value)}
                MenuProps={MenuProps}
              >
                <MenuItem disabled value="">
                  <em>{t("categories.select")}</em>
                </MenuItem>
                {Categoris.map((name) => (
                  <MenuItem
                    key={name.value}
                    value={name.value}
                    sx={{
                      borderRadius: 1,
                      margin: "2px 0",
                      "&.Mui-selected": {
                        backgroundColor: "#644980",
                        color: "#fff",
                        fontWeight: "600",
                        "&:hover": {
                          backgroundColor: "#644980",
                        },
                      },
                      "&:hover": {
                        backgroundColor: "#f3f4f6",
                      },
                    }}
                  >
                    <span>{name.label}</span>
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} md={2}>
              <Tooltip title={t("actions.toggleView")} arrow>
                <IconButton
                  onClick={() => setViewMode(!viewMode)}
                  sx={{
                    backgroundColor: "#f3f4f6",
                    color: "#6b7280",
                    borderRadius: 2,
                    p: 1.5,
                    "&:hover": {
                      backgroundColor: "#e5e7eb",
                      color: "#374151",
                    },
                  }}
                >
                  {viewMode ? (
                    <TableViewIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <ViewModuleIcon sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Content */}
      {!items.length ? (
        <Card sx={{ p: 6, textAlign: "center", borderRadius: 3 }}>
          <Box sx={{ fontSize: 64, color: "#d1d5db", mb: 2 }}>
            <TrendingUp size={64} />
          </Box>
          <Typography variant="h6" sx={{ color: "#6b7280", mb: 1 }}>
            {t("general.NoDataAvailable")}
          </Typography>
          <Typography variant="body2" sx={{ color: "#9ca3af", mb: 3 }}>
            {t("general.noDataMatchingCriteria")}
          </Typography>
          <IconButton
            onClick={() => setIsModalOpen(true)}
            sx={{
              background: "#18181b",
              color: "white",
              p: 2,
              borderRadius: 3,
              "&:hover": {
                background: "#18181b",
              },
              transition: "all 0.3s ease",
            }}
          >
            <Plus size={20} />
          </IconButton>
        </Card>
      ) : (
        <>
          {viewMode ? (
            <Grid container spacing={3}>
              {items
                ?.filter(
                  (item) =>
                    item.name
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()) &&
                    (!filterCategories.length ||
                      filterCategories.includes(item.categories))
                )
                .map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <ProductCard
                      setAction={setEditMode}
                      item={item}
                      onPublish={() =>
                        handlePublish(item.id, !item.isPublished)
                      }
                      onDelete={handleDeleteItem}
                      setMediaModalOpen={setMediaModalOpen}
                    />
                  </Grid>
                ))}
            </Grid>
          ) : (
            <Card
              sx={{ borderRadius: 3, boxShadow: "none", overflow: "hidden" }}
            >
              <TableComponent
                columns={columns}
                data={items}
                rowKeyField="id"
                title={t("products.title")}
                subtitle={`${items.length} products in your catalog`}
                searchable={true}
                sortable={true}
                variant="elevated"
                size="sm"
                maxHeight={window.innerHeight - 320}
                emptyStateMessage="No products found"
                emptyStateSubmessage="Add your first product to start selling"
              />
            </Card>
          )}
        </>
      )}

      {/* Keep existing modals */}
      <AddNewItem
        selectedItem={selectedItem}
        Categoris={Categoris}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedItem(null);
        }}
        onSave={handleAddItem}
        isLoading={isLoading}
        error={error}
      />

      {/* Keep existing MediaModal and ExcelModal */}
      <Dialog
        open={Boolean(MediaModalOpen)}
        onClose={() => setMediaModalOpen(null)}
        sx={{
          "& .MuiPaper-root": {
            margin: "0 10px",
            "@media (min-width: 900px)": {
              maxWidth: "900px",
            },
            "@media (max-width: 899px)": {
              maxWidth: "100%",
              margin: "0",
            },
          },
        }}
      >
        <DialogTitle className="flex justify-between items-center">
          <span>{MediaModalOpen?.name}</span>
          <IconButton onClick={() => setMediaModalOpen(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleMediaSubmit)}
              className="space-y-6"
            >
              <div className="mt-4">
                <FormLabel>{t("product_form.description")}</FormLabel>
                <Tabs defaultValue="en" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="en">English</TabsTrigger>
                    <TabsTrigger value="ar">العربية</TabsTrigger>
                  </TabsList>
                  <TabsContent value="en">
                    <Editor
                      tinymceScriptSrc="/tinymce/tinymce.min.js"
                      onInput={(_: EditorEvent<any>, editor: EditorType) => {
                        form.setValue("descriptionEn", editor.getContent());
                      }}
                      value={form.watch("descriptionEn")}
                      init={{
                        height: 300,
                        plugins: [
                          "advlist",
                          "autolink",
                          "lists",
                          "link",
                          "charmap",
                          "anchor",
                          "searchreplace",
                          "visualblocks",
                          "code",
                          "fullscreen",
                          "insertdatetime",
                          "table",
                          "preview",
                          "help",
                          "wordcount",
                        ],
                        toolbar:
                          "undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help",
                        content_style:
                          "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                        directionality: "ltr",
                      }}
                    />
                  </TabsContent>
                  <TabsContent value="ar">
                    <Editor
                      tinymceScriptSrc="/tinymce/tinymce.min.js"
                      value={form.watch("descriptionAr")}
                      onInput={(_: EditorEvent<any>, editor: EditorType) => {
                        form.setValue("descriptionAr", editor.getContent());
                      }}
                      init={{
                        height: 300,
                        plugins: [
                          "advlist",
                          "autolink",
                          "lists",
                          "link",
                          "charmap",
                          "anchor",
                          "searchreplace",
                          "visualblocks",
                          "code",
                          "fullscreen",
                          "insertdatetime",
                          "table",
                          "preview",
                          "help",
                          "wordcount",
                        ],
                        toolbar:
                          "undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help",
                        content_style:
                          "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                        directionality: "rtl",
                      }}
                    />
                  </TabsContent>
                </Tabs>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-lg font-medium mb-2">
                    {t("supplier_pages.update_product.img")}
                  </h3>
                  <div
                    {...getRootProps()}
                    className={cn(
                      "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
                      isDragActive
                        ? "border-primary bg-primary/5"
                        : "border-muted-foreground/25 hover:border-primary/50"
                    )}
                  >
                    <input {...getInputProps()} />
                    <div className="flex flex-col items-center gap-2">
                      <ImagePlus className="h-10 w-10 text-muted-foreground" />
                      <h4 className="text-base font-medium">
                        {t("product_form.drag_img")}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {t("product_form.req_drag")}
                      </p>
                      <Button
                        variant="outline"
                        type="button"
                        size="sm"
                        className="mt-2"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {t("product_form.browse_files")}
                      </Button>
                    </div>
                  </div>

                  {previewImages.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium mb-2">
                        {t("product_form.uploaded_img")}
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {previewImages.map((imageUrl, index) => (
                          <Card
                            key={index}
                            className="overflow-hidden group relative border"
                          >
                            <img
                              src={imageUrl}
                              alt={`Product image ${index + 1}`}
                              className="w-full h-40 object-cover"
                            />
                            <Button
                              size="icon"
                              variant="destructive"
                              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeImage(index);
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-2">
                    {t("product_form.product_video")}
                  </h3>

                  {!videoPreview ? (
                    <div className="relative">
                      <input
                        type="file"
                        accept="video/*"
                        onChange={handleVideoUpload}
                        className="hidden"
                        id="video-upload"
                      />
                      <label
                        htmlFor="video-upload"
                        className="w-full py-[70px] flex flex-col items-center justify-center border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50 transition-colors"
                      >
                        <Upload className="h-10 w-10 mb-2" />
                        <span className="text-base font-medium">
                          {t("product_form.upload_video")}
                        </span>
                        <span className="text-sm text-muted-foreground mt-1">
                          {t("product_form.req_drag_video")}
                        </span>
                      </label>
                    </div>
                  ) : (
                    <div className="relative">
                      <video
                        src={videoPreview}
                        controls
                        className="w-full rounded-lg"
                      />
                      <Button
                        size="icon"
                        variant="destructive"
                        className="absolute top-2 right-2"
                        onClick={removeVideo}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end mt-8">
                <Button
                  type="submit"
                  className="!bg-black !text-white"
                  disabled={!form.formState.isValid}
                >
                  {isLoading ? t("item.saving") : t("actions.save")}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Excel Import Dialog */}
      <Dialog
        open={isExcelModalOpen}
        onClose={() => {
          setIsExcelModalOpen(false);
          setSelectedExcelFile(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="flex justify-between items-center">
          <span>{t("actions.importExcel")}</span>
          <IconButton onClick={() => setIsExcelModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="space-y-6">
            {/* Download Template Section */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium mb-2">
                {t("actions.downloadTemplate")}
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                {t("actions.downloadTemplateDescription")}
              </p>
              <Button
                onClick={downloadExcelTemplate}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                {t("actions.downloadTemplate")}
              </Button>
            </div>

            {/* File Upload Section */}
            <div>
              <h3 className="text-lg font-medium mb-2">
                {t("actions.uploadFile")}
              </h3>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleExcelFileSelect}
                  className="hidden"
                  id="excel-file-input"
                />
                <label
                  htmlFor="excel-file-input"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <FileSpreadsheet className="h-10 w-10 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium">
                      {selectedExcelFile
                        ? selectedExcelFile.name
                        : t("actions.clickToSelectFile")}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t("actions.supportedFormats")}: .xlsx, .xls, .csv
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Import Button */}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsExcelModalOpen(false)}
              >
                {t("actions.cancel")}
              </Button>
              <Button
                onClick={handleExcelImport}
                disabled={!selectedExcelFile || isImporting}
                className={cn(
                  "flex disabled:bg-none !bg-black !text-white items-center gap-2",
                  !selectedExcelFile || isImporting
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                )}
              >
                {isImporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t("actions.importing")}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    {t("actions.import")}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t("alerts.confirmation.deleteItem")}
        </DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            {t("alerts.confirmation.deleteItemConfirm")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            {t("actions.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            className="!bg-black !text-white"
          >
            {t("actions.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ArticlesList;
